import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import useBfcacheReset from '../useBfcacheReset';
import { paymentErrorMessage } from '../paymentErrors';
import { availableBalance } from '../walletFigures';
// METHOD-label copy is capability-derived, never hardcoded: we do not render an
// Apple Pay method because NI Hosted Sessions is not enabled on the account.
// (The hosted payment page itself does offer Apple Pay on a capable device —
// that truth is stated by SECURE_CHECKOUT_NOTE, not by a method label.)
// See cardRail.js.
import { cardRailLabel } from './cardRail';
// Saved card for quick checkout (item 10, 2026-07-19): the card leg offers
// any active saved card pre-selected, with "Use a new card" as the fallback —
// the same picker the booking modal uses, so the two card legs cannot drift.
import SavedCardPicker, { cardCheckoutBody } from './SavedCardPicker';
import HostedCardPanel from '../payments/HostedCardPanel';
import SavedCardPay from '../payments/SavedCardPay';
import SaveCardNamer from '../payments/SaveCardNamer';
import ReceiptDownload from '../payments/ReceiptDownload';
import { CARD_CONSENT_TEXT } from './cardConsent';
import './depositSplitPanel.css';

// Client-initiated top-ups have a 500 AED minimum (admin-recorded deposits are unrestricted).
const MIN_TOPUP = 500;

// Card top-ups are offered BELOW the bank-transfer tier; at or above it a
// deposit is bank-transfer only. MUST equal the server's ceiling — payments.js
// BANK_TRANSFER_TIER_AED and pendingService.BANK_TRANSFER_TIER_AED are both
// 10000, and their own comment warns "the two rails must agree or a payment can
// be offered by one and refused by the other". This constant was still 5000
// (2026-07-18), so the UI refused card top-ups between AED 5,000 and 10,000
// that the server would have accepted. The 5,000 / 15,000 BONUS tiers are a
// separate rule and are unchanged.
const CARD_TIER_LIMIT = 10000;
const BANK_ONLY_NOTE = 'Top-ups of AED 10,000+ are by bank transfer only — they earn your +5%/+10% bonus.';

// initialAmount (optional): pre-fills the custom amount — used by the booking
// request flow to suggest the wallet shortfall. onSuccess receives the created
// deposit row (existing callers that ignore the argument are unaffected).
// allowCard (default true): the booking-linked flows pass false — a card
// top-up redirects away to the paypage and returns to the wallet page, which
// would abandon the draft booking the deposit was meant to back.
// fixedAmount (optional, number): pre-fills the amount and LOCKS it read-only
// (presets disabled) — no MIN_TOPUP clamping is applied to it.
// purpose (optional, string): sent as `purpose` in the bank-transfer deposit
// POST body. When purpose === 'settle_pending' the form is bank-transfer only
// (card option hidden), a settlement note is shown and no bonus applies.
// When purpose is set the 500 AED client-side minimum is NOT enforced (the
// server owns that rule). Undefined props = previous behavior exactly.
// returnPath (optional, string): sent as `return_path` with the card
// topup-checkout so the paypage returns to the caller's ORIGIN surface
// (one of the /client/payment-return/* routes) instead of always the wallet
// — used by the adjustment top-up flows on the bookings surfaces.
export default function ClientDepositForm({ onClose, onSuccess, initialAmount = null, allowCard = true, fixedAmount = null, purpose = null, returnPath = null, walletClientId = null, walletLabel = null }) {
    // Separate wallets per billing entity: every read and every top-up rail
    // below carries the chosen wallet; null = the main wallet (unchanged).
    const walletQuery = walletClientId ? { params: { wallet: walletClientId } } : {};
    const settlePending = purpose === 'settle_pending';
    const amountLocked = fixedAmount != null && Number(fixedAmount) > 0;
    // Booking shortfalls can be below the minimum — clamp the prefill up to
    // MIN_TOPUP and tell the client the difference stays in their wallet.
    // (fixedAmount is never clamped — the caller fixed it deliberately.)
    const prefillWasRaised = !amountLocked && !!initialAmount && Number(initialAmount) > 0 && Number(initialAmount) < MIN_TOPUP;
    const prefillAmount = amountLocked
        ? Number(fixedAmount)
        : (initialAmount ? Math.max(MIN_TOPUP, Number(initialAmount) || 0) : null);

    const [step, setStep] = useState(1); // 1: amount, 2: method, 3: bank details + upload, 4: confirm, 5: submitted (persistent)
    // Persistent submitted state for the STANDALONE Add-Funds flow (the owner: no
    // auto-dismissing feedback on payment flows). Booking-linked/purpose flows
    // keep firing onSuccess immediately — their parent flow continues.
    const [submittedDeposit, setSubmittedDeposit] = useState(null);
    const [selectedPreset, setSelectedPreset] = useState(null);
    const [customAmount, setCustomAmount] = useState(prefillAmount ? String(prefillAmount) : '');
    const [amountError, setAmountError] = useState('');
    // Card is the DEFAULT method (2026-08-06) — the guard effect below
    // flips to bank_transfer whenever the card rail is unavailable.
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [bankReference, setBankReference] = useState('');
    const [notes, setNotes] = useState('');
    const [file, setFile] = useState(null);
    const [bonus, setBonus] = useState({ bonus_percentage: 0, bonus_amount: 0, total_credit: 0 });
    const [submitting, setSubmitting] = useState(false);
    const [cardSubmitting, setCardSubmitting] = useState(false);
    // Set when the server rejects a card attempt with BANK_TRANSFER_TIER —
    // we switch back to the bank UI and keep the explanation visible.
    const [tierNotice, setTierNotice] = useState(false);
    // --- Combined card capture (2026-07-17: one checkout, officially two
    // payments): while invoices are pending the CARD path offers an opt-in
    // (defaults ON) to take X + P in ONE paypage — P settles the invoices
    // (recorded separately), X tops up the wallet. Item 9 (the owner, ruled twice
    // 2026-07-19): a genuine card TOP-UP earns the SAME bonus tiers as a bank
    // transfer (5% at >= 5,000; the 10,000 card ceiling keeps 10% bank-only) —
    // the settlement portion P never earns anything. CARD path only — the bank
    // rail's topup_with_settlement split is untouched. ---
    // Server said the COMBINED X + P is over the 10,000 card ceiling
    // (BANK_TRANSFER_TIER on a combined attempt) — locks the include toggle
    // until the amount is edited.
    const [combinedTierBlocked, setCombinedTierBlocked] = useState(false);

    // --- Pending-invoices split (2026-07-17, semantics FLIPPED same day):
    // the amount X the client enters/selects is ALWAYS the WALLET TOP-UP — the
    // pending invoices P ride ON TOP of it. We submit amount = X + P tagged
    // topup_with_settlement; the server settles its fresh P first (separate
    // "Pending Invoice Payment" deposit, zero bonus) and the remainder — exactly
    // X — tops up the wallet, so the bonus always lands on the client's chosen
    // figure (picking "5,000 + 5%" earns the 5% regardless of P). Applies ONLY
    // to the plain Add-Funds flow: purpose-driven (settle_pending), fixed-amount
    // and booking/adjustment shortfall flows (initialAmount) keep their exact
    // previous behavior — their deposits must fully back what they were
    // opened for. Display figure only; the server recomputes P fresh. ---
    // FOLD ELIGIBILITY (2026-08-06: booking/adjustment shortfall top-ups
    // must carry the pending invoices too — "fund a booking, skip your
    // invoices" was a side door). Every non-purpose, non-fixed deposit now
    // rides P on top; only the post-submit behavior distinguishes the
    // standalone Add-Funds flow from the booking-linked ones.
    const splitEligible = !purpose && fixedAmount == null;
    const standaloneFlow = splitEligible && initialAmount == null;
    const [pendingInvoicesTotal, setPendingInvoicesTotal] = useState(0);
    // Per-entity split of P (multi-billing clients only — the server sends
    // entity_breakdown: null for everyone else, and the panel then renders
    // exactly as before). DISPLAY ONLY; rides the same read below.
    const [pendingEntityBreakdown, setPendingEntityBreakdown] = useState(null);
    // A FAILED read is not "no debt" (Lilauli 2026-08-06: the breakdown call
    // failed, the form showed no pending panel, and a plain AED 500 top-up
    // went through over AED 2,708 of open invoices). The server now folds the
    // debt into the capture regardless, so the card button must not promise a
    // figure we could not verify — fail closed until the read succeeds.
    const [pendingReadFailed, setPendingReadFailed] = useState(false);
    // IN-FLIGHT is not "no debt" either (2026-08-09): every proceed/pay action stays
    // disabled with a checking label until this read RESOLVES — success,
    // degraded or failed. Complements the fail-closed guard below, which only
    // covers the after-failure state.
    const [pendingCheckDone, setPendingCheckDone] = useState(false);
    const [pendingReadNonce, setPendingReadNonce] = useState(0);
    useEffect(() => {
        if (!splitEligible) return undefined;
        let cancelled = false;
        setPendingCheckDone(false);
        axios.get('/api/client-requests/invoices/pending-breakdown', walletQuery)
            .then((res) => {
                if (cancelled) return;
                // zoho_ok=false is a DEGRADED response: the endpoint returns
                // total 0 with a flag rather than erroring (display design).
                // For a money gate, unverified ≠ zero — treat it as a failed
                // read (this, not an HTTP error, is what happened to Lilauli).
                if (res.data?.zoho_ok === false) { setPendingReadFailed(true); return; }
                setPendingInvoicesTotal(Math.max(0, Number(res.data?.total || 0)));
                setPendingEntityBreakdown(
                    Array.isArray(res.data?.entity_breakdown) && res.data.entity_breakdown.length > 0
                        ? res.data.entity_breakdown
                        : null
                );
                setPendingReadFailed(false);
            })
            .catch(() => { if (!cancelled) setPendingReadFailed(true); })
            .finally(() => { if (!cancelled) setPendingCheckDone(true); });
        return () => { cancelled = true; };
    }, [splitEligible, pendingReadNonce]);
    const splitP = splitEligible ? pendingInvoicesTotal : 0;
    const splitActive = splitP > 0.009;

    // --- Wallet context (2026-07-18): the client must see where they are
    // and where this top-up lands BEFORE they pay. Purely additive and
    // best-effort — a failed fetch just hides the panel, it never blocks the
    // flow. Available (not total) is the "now" figure, matching every other
    // client surface; bonus is included in the "after" figure because it is
    // credited on approval. ---
    const [wallet, setWallet] = useState(null);
    useEffect(() => {
        let cancelled = false;
        axios.get('/api/client-requests/wallet', walletQuery)
            .then((res) => { if (!cancelled) setWallet(res.data || null); })
            .catch(() => { /* context only — never break Add Funds */ });
        return () => { cancelled = true; };
    }, []);

    // --- Saved card for quick checkout (item 10, 2026-07-19): when an
    // active saved card exists, offer it PRE-SELECTED on the card leg so the
    // paypage pre-fills the stored card and the client retypes nothing.
    // "Use a new card" is always one tap away. Best-effort — a failed fetch
    // just means the plain new-card paypage, exactly as before. ---
    const [savedCards, setSavedCards] = useState([]);
    const [cardSelection, setCardSelection] = useState('new');
    // EMBEDDED card rail (Hosted Session, 2026-08-13): 'probe' renders the
    // panel, which downgrades itself to 'off' (→ legacy redirect button) when
    // the rail is disabled or the SDK can't mount. Never a broken embed.
    // v1 scope: plain wallet top-ups only — purpose/fixedAmount surfaces keep
    // the redirect until each is wired to its own hosted-session endpoint.
    const [hostedRail, setHostedRail] = useState('probe');
    // Save-card consent on the embedded card step (step 6). Storing requires
    // the explicit tick — the server enforces the same rule.
    const [saveNewCard, setSaveNewCard] = useState(false);
    // In-modal success (2026-08-13): a card payment must NOT slam the modal
    // shut — the client stays here and sees what happened and their new balance.
    const [savedCvv, setSavedCvv] = useState('');
    const [savedCardBusy, setSavedCardBusy] = useState(false);
    const [cardPaid, setCardPaid] = useState(null); // { amount, bonus, balance, savedCard }
    // The Zoho receipt for the top-up just paid (2026-08-14) — same offer
    // the invoice payment page makes, from the same kind of EXACT handle: this
    // deposit's own id, never "the newest payment". The deposit is approved and
    // its Zoho payment created moments after the capture, so it arrives by
    // polling; a missing receipt simply hides the link.
    // The action row's PAY position on each card step. Held in state (not a
    // ref) so the child components re-render once the node exists and can
    // portal their button into it — see SavedCardPay / HostedCardPanel.
    const [paySlot, setPaySlot] = useState(null);
    const [cardPaySlot, setCardPaySlot] = useState(null);

    // THE RECEIPT LINK. No lookup here at all — the server resolves the Zoho
    // payment from these handles and streams the PDF in one call.
    const receiptHref = useCallback((depositId, orderRef, part) => {
        // No handle, no link. Rendering one anyway is what produced
        // "deposit_id or order_ref is required" where a PDF should have been.
        if (!depositId && !orderRef) return null;
        const q = new URLSearchParams();
        if (depositId) q.set('deposit_id', String(depositId));
        if (orderRef) q.set('order_ref', orderRef);
        if (part) q.set('part', part);
        return `/api/client-requests/wallet/receipt-pdf?${q.toString()}`;
    }, []);

    // Name the just-saved card without leaving the success step.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get('/api/cards');
                if (cancelled) return;
                const list = Array.isArray(res?.data?.cards) ? res.data.cards : [];
                setSavedCards(list);
                // Most recent card first (the server orders them) — pre-select it.
                if (list.length > 0) setCardSelection(list[0].id);
            } catch { /* no saved cards — the new-card paypage still works */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const presets = [
        { amount: 2500, label: '2,500 AED', bonus: 'No Bonus' },
        { amount: 5000, label: '5,000 AED', bonus: '+5% Bonus' },
        { amount: 15000, label: '15,000 AED', bonus: '+10% Bonus' },
    ];

    const getAmount = () => {
        if (selectedPreset !== null) return presets[selectedPreset].amount;
        return parseFloat(customAmount) || 0;
    };

    // Calculate bonus when amount changes. Settlement deposits
    // (purpose === 'settle_pending') never earn a bonus. The entered amount X
    // is ALWAYS the wallet top-up (pending invoices ride ON TOP), so the bonus
    // is judged on X directly — even when the split is active.
    useEffect(() => {
        const amount = getAmount();
        if (settlePending) {
            setBonus({ bonus_percentage: 0, bonus_amount: 0, total_credit: amount });
        } else if (amount >= 10) {
            let bonus_percentage = 0;
            if (amount >= 15000) bonus_percentage = 10;
            else if (amount >= 5000) bonus_percentage = 5;
            const bonus_amount = parseFloat((amount * bonus_percentage / 100).toFixed(2));
            const total_credit = parseFloat((amount + bonus_amount).toFixed(2));
            setBonus({ bonus_percentage, bonus_amount, total_credit });
        } else {
            setBonus({ bonus_percentage: 0, bonus_amount: 0, total_credit: 0 });
        }
    }, [selectedPreset, customAmount, settlePending]); // eslint-disable-line react-hooks/exhaustive-deps

    // Restored from the paypage's Back button (bfcache) → the "Opening secure
    // payment…" freeze from right before the redirect must clear.
    useBfcacheReset(() => setCardSubmitting(false));

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
        }
    }, []);

    // Rejected drops (oversize / wrong type) must SAY so — a silently ignored
    // bank-receipt drop looks like the app is broken.
    const onDropRejected = useCallback((fileRejections) => {
        const rejection = fileRejections?.[0];
        const code = rejection?.errors?.[0]?.code;
        if (code === 'file-too-large') {
            toast.error('That file is over 10MB — please upload a smaller image or PDF.');
        } else if (code === 'file-invalid-type') {
            toast.error('That file type isn’t supported — please upload a JPEG, PNG, WebP or PDF.');
        } else {
            toast.error('That file couldn’t be added — please upload one JPEG, PNG, WebP or PDF up to 10MB.');
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/webp': ['.webp'],
            'application/pdf': ['.pdf']
        },
        maxSize: 10 * 1024 * 1024,
        maxFiles: 1
    });

    const handleSubmit = async () => {
        const amount = getAmount();
        // purpose-driven deposits (e.g. settle_pending) skip the client-side
        // minimum — the server enforces its own rule for them. In split mode
        // the minimum applies to X, the wallet top-up (P rides on top of it).
        if (!purpose && amount < MIN_TOPUP) {
            toast.error(`Minimum top-up is AED ${MIN_TOPUP}.`);
            return;
        }
        if (!file) {
            toast.error('Please upload proof of payment');
            return;
        }

        setSubmitting(true);
        try {
            // Plain Add-Funds bank transfer while invoices are pending → ONE
            // request tagged topup_with_settlement carrying the TOTAL transferred
            // (X + P): the server settles the fresh P first (separate Pending
            // Invoice Payment deposit) and tops up the wallet with the remainder
            // — exactly X, so the bonus lands on the client's chosen figure.
            const effectivePurpose = purpose || (splitActive ? 'topup_with_settlement' : null);
            const totalToSend = splitActive ? parseFloat((amount + splitP).toFixed(2)) : amount;
            const formData = new FormData();
            formData.append('amount', totalToSend);
            formData.append('payment_method', paymentMethod);
            formData.append('bank_reference', bankReference);
            formData.append('notes', notes);
            if (effectivePurpose) formData.append('purpose', effectivePurpose);
            // The pending total P this split was CUT against. Without it the
            // server's drift guard (assertNoPendingDrift) returns immediately
            // and silently re-cuts the split against a fresh P — which can drop
            // the top-up below the bonus tier the client was promised while the
            // confirmation screen still shows the old figures. With it, a moved
            // P comes back as a 400 the catch below adopts and re-confirms.
            if (splitActive) formData.append('expected_total', splitP);
            formData.append('proof_of_payment', file);

            if (walletClientId) formData.append('wallet_client_id', String(walletClientId));
            const res = await axios.post('/api/client-requests/wallet/deposits', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (standaloneFlow) {
                // Standalone Add Funds → PERSISTENT confirmation step (stays
                // until the client clicks Done — no toast, no timer).
                setSubmittedDeposit(res.data);
                setStep(5);
            } else {
                // Purpose/booking-linked flows continue immediately in their
                // parent (which shows its own persistent confirmation). A
                // SPLIT response carries two rows — the booking must link the
                // TOP-UP row (it exactly backs the booking); the settlement
                // row rides to admin review on its own.
                toast.success('Deposit request submitted successfully! It will be reviewed shortly.');
                onSuccess(res.data?.topup_deposit ?? res.data);
            }
        } catch (err) {
            // The server recomputes P fresh on submit — if the pending total
            // changed since the panel loaded, it 400s with the fresh figure
            // (PENDING_DRIFT carries it in breakdown.total; older responses
            // put it in total). Adopt it so every panel/summary row re-renders
            // with the new P, then let the client re-confirm.
            const freshTotal = Number(err.response?.data?.breakdown?.total ?? err.response?.data?.total);
            if (splitEligible && err.response?.status === 400 && Number.isFinite(freshTotal) && freshTotal > 0) {
                setPendingInvoicesTotal(freshTotal);
                toast.error(`Your outstanding total changed to AED ${freshTotal.toFixed(2)} — review and confirm.`);
            } else {
                console.error('Error submitting deposit:', err);
                toast.error(paymentErrorMessage(err, 'Failed to submit deposit'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const amount = getAmount();
    // Split mode: what the bank actually receives — the wallet top-up X plus
    // the pending invoices P riding on top. Equals X when no split is active.
    const totalTransfer = splitActive ? parseFloat((amount + splitP).toFixed(2)) : amount;
    // The pending check must have RESOLVED before the client can move toward
    // any payment (2026-08-09) — they see their pending amount first. And
    // a FAILED check blocks adding funds entirely (same ruling): unverified
    // debt means no deposit of any kind until the read succeeds.
    const pendingCheckPending = splitEligible && !pendingCheckDone;
    const pendingCheckBlocked = splitEligible && pendingCheckDone && pendingReadFailed;
    const canProceedStep1 = amount > 0 && !pendingCheckPending && !pendingCheckBlocked;
    const canProceedStep3 = !!file;
    // settle_pending deposits are bank-transfer only — the card option is
    // hidden entirely.
    const cardAllowed = allowCard && !settlePending && amount < CARD_TIER_LIMIT
        && !(splitEligible && pendingReadFailed);

    // Combined card capture shares the 10,000 card ceiling: when X + P is over
    // it the include toggle locks (client-side pre-check; the server enforces
    // the same gate with BANK_TRANSFER_TIER) — the plain X top-up stays possible.
    // Balance now → balance after this top-up. The credit that actually lands in
    // the wallet is the top-up X plus its bonus (settlement money never lands in
    // the wallet, so splitP is deliberately excluded).
    const walletAvailableNow = wallet ? availableBalance(wallet) : null;
    const creditLanding = settlePending ? 0 : Math.round((amount + (bonus.bonus_amount || 0)) * 100) / 100;
    const availableAfter = walletAvailableNow != null
        ? Math.round((walletAvailableNow + creditLanding) * 100) / 100
        : null;

    const combinedOverTier = splitActive && totalTransfer > CARD_TIER_LIMIT;
    const includeBlocked = combinedOverTier || combinedTierBlocked;
    // FORCED (2026-07-19). Whenever a
    // top-up carries pending invoices and the combined total fits the card
    // ceiling, the invoices ride the same payment. No opt-out. Over the ceiling
    // (`includeBlocked`) the card can't carry them, so the UI directs the client
    // to settle the invoices by bank transfer instead.
    const includeInvoices = splitActive && !includeBlocked;

    // Editing the amount invalidates a server-side combined-tier rejection —
    // the client-side combinedOverTier check re-covers it from fresh figures.
    useEffect(() => {
        setCombinedTierBlocked(false);
    }, [selectedPreset, customAmount]);

    // If the amount is edited back above the tier while Card was selected,
    // fall back to bank transfer (the card option disappears).
    useEffect(() => {
        if (!cardAllowed && paymentMethod === 'card') setPaymentMethod('bank_transfer');
    }, [cardAllowed, paymentMethod]);

    // Pay by card: create the hosted-checkout order and hand the browser
    // to the paypage. The wallet page handles the return (?payment=return&ref=…)
    // and verifies the outcome against the gateway — nothing is credited here.
    // A SAVED card may be picked (item 10 — payment_method_id pre-fills the
    // paypage; the token never reaches this client), but the form still never
    // SAVES a new card: no save_card intent is ever sent from here, so the
    // new-card leg stays a plain one-off checkout (2026-07-17).
    const handleCardCheckout = async () => {
        if (!purpose && amount < MIN_TOPUP) {
            toast.error(`Minimum top-up is AED ${MIN_TOPUP}.`);
            return;
        }
        // Snapshot the opt-in: when ON we send { amount: X, include_pending: true }
        // and the gateway captures X + P in one paypage (combined capture —
        // 2026-07-17: one checkout, officially two payments).
        const combined = includeInvoices;
        setCardSubmitting(true);
        try {
            // COMBINED capture: the button promises X + P, but the server
            // recomputes P fresh when it stakes the gateway order. Re-read P
            // immediately before staking and refuse to hand the client to a
            // paypage for a figure they never saw — the window shrinks from
            // "however long the panel was open" to a few hundred ms, and no
            // gateway order is created on drift.
            if (combined) {
                try {
                    const fresh = await axios.get('/api/client-requests/invoices/pending-breakdown', walletQuery);
                    const freshP = Math.max(0, Number(fresh.data?.total || 0));
                    if (Math.abs(freshP - splitP) > 0.01) {
                        setPendingInvoicesTotal(freshP);
                        toast.error(`Your outstanding total changed to AED ${freshP.toFixed(2)} — review and confirm.`);
                        setCardSubmitting(false);
                        return;
                    }
                } catch {
                    // Breakdown unavailable — fall through and let the server's
                    // own guard (expected_total below) be the backstop.
                }
            }
            // expected_total = the combined figure printed on the button, so the
            // server can reject a capture that would differ from the promise.
            // cardCheckoutBody: a picked SAVED card rides as payment_method_id
            // (the server resolves the token; the paypage pre-fills). 'new'
            // adds nothing — saveNew is pinned false, this form never saves.
            const body = combined
                ? { amount, include_pending: true, expected_total: totalTransfer, ...cardCheckoutBody(cardSelection, false) }
                : { amount, ...cardCheckoutBody(cardSelection, false) };
            // Origin-surface return (adjustment top-ups etc.): the paypage
            // comes back to the caller's /client/payment-return/* route.
            if (returnPath) body.return_path = returnPath;
            if (walletClientId) body.wallet_client_id = walletClientId;
            const res = await axios.post('/api/payments/topup-checkout', body);
            if (res.data?.payment_url) {
                window.location.assign(res.data.payment_url);
                return; // no state reset — we are navigating away
            }
            // No paypage URL → surface it instead of navigating to "<origin>/null"
            // while a pending deposit + created checkout sit behind the scenes.
            toast.error('Could not open the secure payment page. Please try again.');
            setCardSubmitting(false);
            return;
        } catch (err) {
            if (err.response?.data?.code === 'PENDING_DRIFT') {
                // The pending-invoices total changed since the panel loaded —
                // adopt the fresh figure inline and let the client re-confirm.
                const fresh = Number(err.response?.data?.breakdown?.total
                    ?? err.response?.data?.pending_total ?? err.response?.data?.total);
                if (Number.isFinite(fresh)) setPendingInvoicesTotal(Math.max(0, fresh));
                toast.error(`Your outstanding total changed to AED ${Number.isFinite(fresh) ? fresh.toFixed(2) : splitP.toFixed(2)} — review and confirm.`);
            } else if (err.response?.data?.code === 'BANK_TRANSFER_TIER') {
                if (combined) {
                    // The COMBINED X + P crossed the 10,000 card ceiling
                    // server-side (P may have grown since the panel loaded).
                    // Adopt the fresh figure and lock the include toggle —
                    // the plain X card top-up stays possible.
                    const freshP = Number(err.response?.data?.pending_total);
                    if (Number.isFinite(freshP) && freshP > 0) setPendingInvoicesTotal(freshP);
                    setCombinedTierBlocked(true);
                } else {
                    // Server says this amount is in the bank-only tier — switch back
                    // to the bank UI with the note explaining why.
                    setPaymentMethod('bank_transfer');
                    setTierNotice(true);
                }
            } else {
                console.error('Error starting card top-up:', err);
                toast.error(paymentErrorMessage(err, 'Could not start the card payment.'));
            }
            setCardSubmitting(false);
        }
    };

    // Validate the 500 AED minimum on Continue (presets are all above it).
    // Skipped for purpose-driven deposits — the server owns their minimum.
    // In split mode the minimum applies to X, the wallet top-up (P rides on top).
    // EMBEDDED rail start: the panel hands us the SDK's one-time sessionId and
    // this posts it to the hosted-session twin of /topup-checkout. The server
    // recomputes the pending combination fresh, exactly like the redirect rail.
    const startHostedPayment = async (sessionId) => {
        if (!purpose && amount < MIN_TOPUP) {
            toast.error(`Minimum top-up is AED ${MIN_TOPUP}.`);
            return null;
        }
        try {
            const res = await axios.post('/api/payments/hosted-session/topup', {
                wallet_client_id: walletClientId || undefined,
                amount,
                session_id: sessionId,
                ...(saveNewCard ? { save_card: true, save_card_consent: true } : {}),
            });
            return res.data;
        } catch (err) {
            if (err.response?.data?.code === 'BANK_TRANSFER_TIER') {
                const freshP = Number(err.response?.data?.pending_total);
                if (Number.isFinite(freshP) && freshP > 0) {
                    setPendingInvoicesTotal(freshP);
                    setCombinedTierBlocked(true);
                } else {
                    setPaymentMethod('bank_transfer');
                }
                toast.error(err.response.data.error);
                return null;
            }
            toast.error(err.response?.data?.error || 'Could not start the card payment. Please try again.');
            return null;
        }
    };

    const embeddedCardActive = paymentMethod === 'card'
        && cardSelection === 'new'
        && hostedRail !== 'off'
        && !purpose && !fixedAmount;
    // A picked SAVED card pays in place too (CVV only) — same step, no paypage.
    const savedCardActive = paymentMethod === 'card'
        && typeof cardSelection === 'number'
        && hostedRail !== 'off'
        && !purpose && !fixedAmount;
    const selectedSavedCard = savedCards.find((c) => c.id === cardSelection) || null;

    // Charge the stored token with the code the client just typed. The CVV
    // exists only inside this call.
    const chargeSavedCard = async (cvv) => {
        try {
            const res = await axios.post('/api/payments/saved-card-topup', {
                wallet_client_id: walletClientId || undefined,
                amount,
                payment_method_id: cardSelection,
                cvv,
            });
            return res.data;
        } catch (err) {
            if (err.response?.data?.code === 'BANK_TRANSFER_TIER') {
                setPaymentMethod('bank_transfer');
                toast.error(err.response.data.error);
                return null;
            }
            throw err;
        }
    };

    const handleContinueStep1 = () => {
        if (!purpose && amount < MIN_TOPUP) {
            setAmountError(`Minimum top-up is AED ${MIN_TOPUP}.`);
            return;
        }
        setAmountError('');
        setStep(2);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Copied to clipboard');
        }).catch(() => {
            toast.error('Failed to copy');
        });
    };

    // After the persistent submitted step, EVERY way out (Done, X, backdrop)
    // completes the flow so the parent refreshes its lists.
    const closeOrFinish = () => {
        if (step === 5 && submittedDeposit) onSuccess(submittedDeposit);
        else onClose();
    };

    return (
        <div className="cwallet__depositOverlay" onClick={(e) => { if (e.target === e.currentTarget) closeOrFinish(); }}>
            <div className="cwallet__depositModal">
                <button type="button" className="modal-x" onClick={closeOrFinish}>&times;</button>
                <h2 className="cwallet__depositTitle">
                    {walletClientId && walletLabel ? `Add Funds — ${walletLabel}` : 'Add Funds'}
                </h2>
                {walletClientId && walletLabel && (
                    <div className="cwallet__minTopupNote">
                        This top-up goes to the {walletLabel} wallet and is recorded on that entity's account.
                    </div>
                )}

                {settlePending && (
                    <div className="cwallet__minTopupNote">
                        This payment settles your pending invoices.
                    </div>
                )}

                {/* Step indicator. The bank path is 4 steps; the card path is
                    2 — except the EMBEDDED new-card rail, which adds its own
                    card-entry step (6). Without that third dot the modal looked
                    finished while a whole step remained (2026-08-13). The
                    success step (7) and the bank submitted step (5) show none. */}
                {step !== 5 && step !== 7 && (
                <div className="cwallet__stepIndicator">
                    {(paymentMethod === 'card'
                        ? (embeddedCardActive || step === 6 ? [1, 2, 6] : [1, 2])
                        : [1, 2, 3, 4]
                    ).map((s) => (
                        <div
                            key={s}
                            className={`cwallet__stepDot ${step === s ? 'is-active' : ''} ${step > s ? 'is-done' : ''}`}
                        />
                    ))}
                </div>
                )}

                {/* Step 1: Amount */}
                {step === 1 && (
                    <>
                        {!settlePending && (
                        <div className="cwallet__bonusOffer">
                            <div className="cwallet__bonusOfferTitle">🎁 Introductory Bonus Credit Offers</div>
                            <div className="cwallet__bonusOfferText">
                                Top up <strong>5,000 AED</strong> and get <strong>+5% bonus</strong> credit, or top up
                                <strong> 15,000 AED</strong> and get <strong>+10% bonus</strong>. Bonus is added instantly
                                upon approval and used automatically toward your bookings.
                            </div>
                        </div>
                        )}

                        <div className="cwallet__inputLabel">{amountLocked ? 'Amount' : 'Select Amount'}</div>
                        <div className="cwallet__amountPresets">
                            {/* 1. Custom amount box */}
                            <div
                                className={`cwallet__presetBtn cwallet__presetBtn--custom ${selectedPreset === null && customAmount ? 'is-selected' : ''}`}
                                onClick={() => { if (!amountLocked) setSelectedPreset(null); }}
                            >
                                <div className="cwallet__presetAmount">{amountLocked ? 'Fixed Amount' : 'Custom Amount'}</div>
                                <input
                                    type="number"
                                    className="cwallet__customAmountInput"
                                    placeholder={amountLocked ? undefined : `Min ${MIN_TOPUP} AED`}
                                    value={customAmount}
                                    min="500"
                                    readOnly={amountLocked}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        if (amountLocked) return;
                                        setCustomAmount(e.target.value); setSelectedPreset(null); setAmountError('');
                                    }}
                                />
                            </div>
                            {/* 2, 3, 4. Preset boxes (locked out when the amount is fixed) */}
                            {presets.map((p, i) => (
                                <button
                                    key={i}
                                    className={`cwallet__presetBtn ${selectedPreset === i ? 'is-selected' : ''}`}
                                    disabled={amountLocked}
                                    onClick={() => {
                                        if (amountLocked) return;
                                        setSelectedPreset(i); setCustomAmount(''); setAmountError('');
                                    }}
                                >
                                    <div className="cwallet__presetAmount">{p.label}</div>
                                    <span className="cwallet__presetBonus">{p.bonus}</span>
                                </button>
                            ))}
                        </div>

                        {amountError && (
                            <div className="cwallet__amountError">{amountError}</div>
                        )}
                        {/* Honest about the raise (2026-07-18): a client who
                            was short AED 120 is being asked for 500, so name the
                            extra instead of silently bumping the prefill. */}
                        {prefillWasRaised && (
                            <div className="cwallet__minTopupNote">
                                Top-ups start at AED 500 — you only needed {money(Number(initialAmount))}, so the
                                extra {money(Math.max(0, MIN_TOPUP - Number(initialAmount)))} stays in your wallet
                                for future bookings.
                            </div>
                        )}

                        {/* ONE summary card (2026-08-06) — what you pay, the bonus, what rides for
                            invoices, and where the wallet ends up. Rows appear
                            only when they carry a real figure. */}
                        <div className="cwsplit__panel">
                            {/* The pending-invoices callout stays whenever debt
                                rides (2026-08-06) — one line, inside the card. */}
                            {splitActive && (
                                <div className="cwsplit__title">
                                    You have pending invoices — they are added on top of your top-up
                                </div>
                            )}
                            <div className="cwsplit__row">
                                <span>{settlePending ? 'Pending invoice payment' : 'Wallet top-up'}</span>
                                <strong>{amount > 0 ? money(amount) : '—'}</strong>
                            </div>
                            {bonus.bonus_amount > 0 && (
                                <div className="cwsplit__row">
                                    <span>Bonus ({bonus.bonus_percentage}%) — credited to your wallet</span>
                                    <strong>+{money(bonus.bonus_amount)}</strong>
                                </div>
                            )}
                            {splitActive && (
                                <div className="cwsplit__row cwsplit__row--settle">
                                    <span>Pending invoices settled on top</span>
                                    <strong>{money(splitP)}</strong>
                                </div>
                            )}
                            {/* Per-entity split of P (multi-billing clients only —
                                null for everyone else, so nothing extra renders). */}
                            {splitActive && pendingEntityBreakdown && pendingEntityBreakdown.map((e, i) => (
                                <div key={i} className="cwsplit__row cwsplit__row--entity">
                                    <span>{e.label}</span>
                                    <strong>{money(e.amount)}</strong>
                                </div>
                            ))}
                            <div className="cwsplit__row cwsplit__row--total">
                                <span>Total to pay</span>
                                <strong>{amount > 0 ? money(totalTransfer) : '—'}</strong>
                            </div>
                            {!settlePending && walletAvailableNow != null && (
                                <div className="cwsplit__row" style={{ opacity: 0.75 }}>
                                    {/* Unclamped (2026-07-18): a negative available
                                        must show as the true figure. */}
                                    <span>Available after this top-up (now {money(walletAvailableNow)})</span>
                                    <strong>{amount > 0 ? money(availableAfter) : money(walletAvailableNow)}</strong>
                                </div>
                            )}
                            {paymentMethod !== 'card' && !settlePending && (
                                <div className="cwallet__afterNote">
                                    Credited once we approve your transfer — usually within a few hours.
                                </div>
                            )}
                        </div>

                        <div className="cwallet__depositActions">
                            <button className="cwallet__depositBtn cwallet__depositBtn--secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                className="cwallet__depositBtn cwallet__depositBtn--primary"
                                disabled={!canProceedStep1}
                                onClick={handleContinueStep1}
                            >
                                {pendingCheckPending ? 'Checking your account…' : 'Continue'}
                            </button>
                        </div>
                        {/* Pending-check status, visible the moment the form opens
                            (2026-08-09): checking → say so; failed → funds are
                            blocked entirely until a retry succeeds. */}
                        {pendingCheckPending && (
                            <div className="cwallet__pendingCheckNote">
                                Checking your outstanding invoices…
                            </div>
                        )}
                        {pendingCheckBlocked && (
                            <div className="cwallet__pendingCheckError">
                                We couldn’t check your outstanding invoices, so funds can’t be
                                added right now.{' '}
                                <button
                                    type="button"
                                    className="cwallet__pendingCheckRetry"
                                    onClick={() => setPendingReadNonce((n) => n + 1)}
                                >
                                    Try again
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Step 2: Payment Method */}
                {step === 2 && (
                    <>
                        <div className="cwallet__inputLabel">Payment Method</div>
                        {!savedCardBusy && <div className="cwallet__methodBtns">
                            {settlePending ? (
                                /* settle_pending: bank transfer only — no card option at all */
                                null
                            ) : cardAllowed ? (
                                <button
                                    className={`cwallet__methodBtn ${paymentMethod === 'card' ? 'is-selected' : ''}`}
                                    onClick={() => setPaymentMethod('card')}
                                >
                                    {cardRailLabel()}
                                    <span className="cwallet__methodHint">
                                        {hostedRail !== 'off' && !purpose && !fixedAmount
                                            ? 'Instant — pay right here'
                                            : 'Instant — secure payment page'}
                                    </span>
                                </button>
                            ) : splitEligible && pendingReadFailed && allowCard ? (
                                /* The outstanding-invoices read failed — a card
                                   capture folds those invoices in, so we refuse to
                                   promise a figure we could not verify. Tapping
                                   simply retries the read. */
                                <button
                                    className="cwallet__methodBtn"
                                    onClick={() => setPendingReadNonce((n) => n + 1)}
                                >
                                    {cardRailLabel()}
                                    <span className="cwallet__comingSoon">
                                        Couldn’t check your account balance — tap to retry.
                                    </span>
                                </button>
                            ) : allowCard ? (
                                /* ≥ 10,000 AED — bank-transfer only (card ceiling).
                                   Bonus tiers are amount-based and rail-agnostic:
                                   a 5,000–9,999 CARD top-up earns its 5% too (item 9). */
                                <button className="cwallet__methodBtn" disabled>
                                    {cardRailLabel()}
                                    <span className="cwallet__comingSoon">Bank transfer only at this amount</span>
                                </button>
                            ) : (
                                /* Booking-linked top-up flows (allowCard=false): card
                                   payments ARE live product-wide — this deposit just has
                                   to stay attached to the booking it backs, which the
                                   redirect flow can't do. Say that, truthfully. */
                                <button className="cwallet__methodBtn" disabled>
                                    {cardRailLabel()}
                                    <span className="cwallet__comingSoon">
                                        Not available for this top-up — to pay by card, choose “Pay now by card” on the booking instead.
                                    </span>
                                </button>
                            )}
                            <button
                                className={`cwallet__methodBtn ${paymentMethod === 'bank_transfer' ? 'is-selected' : ''}`}
                                onClick={() => setPaymentMethod('bank_transfer')}
                            >
                                Bank Transfer
                            </button>
                        </div>

                        }
                        {!savedCardBusy && allowCard && !settlePending && (amount >= CARD_TIER_LIMIT || tierNotice) && (
                            <div className="cwallet__bankOnlyNote">{BANK_ONLY_NOTE}</div>
                        )}

                        {/* Saved card for quick checkout (item 10): pre-selected;
                            "Use a new card" is the fallback. allowSaveNew off —
                            this form never saves a card (see handleCardCheckout). */}
                        {!savedCardBusy && paymentMethod === 'card' && cardAllowed && savedCards.length > 0 && (
                            <div className="cwallet__savedCardPick">
                                <SavedCardPicker
                                    cards={savedCards}
                                    value={cardSelection}
                                    onChange={setCardSelection}
                                    allowSaveNew={false}
                                    disabled={cardSubmitting || savedCardBusy}
                                    cvvValue={savedCardActive ? savedCvv : null}
                                    onCvvChange={savedCardActive ? setSavedCvv : null}
                                    newCardSub={hostedRail !== 'off' && !purpose && !fixedAmount
                                        ? 'Continue to input card details or select digital payments.'
                                        : null}
                                />
                            </div>
                        )}

                        {/* Card + pending invoices: opt-in COMBINED capture (defaults
                            ON) — one paypage takes X + P; P settles the invoices
                            (recorded separately), X tops up the wallet. Over the
                            10,000 card ceiling the toggle locks and only the plain
                            X top-up remains; toggled off = the old passive notice. */}
                        {/* Over the 10,000 card ceiling: the invoices can't ride
                            the card, so send them to the bank-transfer flow. */}
                        {splitActive && paymentMethod === 'card' && !includeInvoices && (
                            <div className="cwsplit__cardNotice">
                                <div style={{ fontWeight: 600 }}>
                                    Combined total {money(totalTransfer)} is over 10,000 — pay this top-up by
                                    card, and settle your {money(splitP)} pending invoices by bank transfer
                                    (which includes them automatically).
                                </div>
                            </div>
                        )}

                        {/* Pending invoices ride every top-up — the SAME breakdown
                            panel for card and bank (2026-08-06: no yellow box,
                            no extra caption; one presentation everywhere). */}
                        {splitActive && (paymentMethod === 'bank_transfer' || includeInvoices) && (
                            <div className="cwsplit__panel">
                                <div className="cwsplit__row">
                                    <span>Wallet top-up{bonus.bonus_amount > 0 ? ` (bonus ${money(bonus.bonus_amount)} applies to this)` : ''}</span>
                                    <strong>{money(amount)}</strong>
                                </div>
                                <div className="cwsplit__row cwsplit__row--settle">
                                    <span>Pending invoices settled on top (Pending Invoice Payment)</span>
                                    <strong>{money(splitP)}</strong>
                                </div>
                                {/* Per-entity split of P (multi-billing only). */}
                                {pendingEntityBreakdown && pendingEntityBreakdown.map((e, i) => (
                                    <div key={i} className="cwsplit__row cwsplit__row--entity">
                                        <span>{e.label}</span>
                                        <strong>{money(e.amount)}</strong>
                                    </div>
                                ))}
                                <div className="cwsplit__row cwsplit__row--total">
                                    <span>Total to pay</span>
                                    <strong>{money(totalTransfer)}</strong>
                                </div>
                            </div>
                        )}

                        {/* While a saved card is paying the modal shows ONLY the
                            payment status (2026-08-13) — amount, methods and
                            picker step aside so there is one thing to look at,
                            and it returns to normal (with the error) on failure. */}
                        {savedCardActive && selectedSavedCard && (
                            <SavedCardPay
                                card={selectedSavedCard}
                                compact
                                cvv={savedCvv}
                                onCvvChange={setSavedCvv}
                                onBusyChange={setSavedCardBusy}
                                actionSlot={paySlot}
                                payLabel={`Pay ${money(splitActive ? totalTransfer : amount)}`}
                                disabled={pendingCheckPending || (!purpose && amount < MIN_TOPUP)}
                                charge={chargeSavedCard}
                                onPaid={(result) => {
                                    // Straight to success — no gap where the form
                                    // could reappear (2026-08-13). The balance
                                    // line fills in a moment later.
                                    setCardPaid({
                                        amount: splitActive ? totalTransfer : amount,
                                        topup: amount,
                                        invoices: splitActive ? splitP : 0,
                                        bonus: bonus.bonus_amount || 0,
                                        balance: null,
                                        deposit_id: result?.deposit_id || null,
                                        order_ref: result?.order_ref || null,
                                        savedCard: null,
                                    });
                                    setStep(7);
                                    axios.get('/api/client-requests/wallet', walletQuery)
                                        .then((w) => {
                                            setWallet(w.data || null);
                                            setCardPaid((prev) => (prev ? { ...prev, balance: availableBalance(w.data || {}) } : prev));
                                        })
                                        .catch(() => {});
                                }}
                                onFailed={() => { /* the row shows its own message */ }}
                            />
                        )}

                        <div className="cwallet__depositActions">
                            <button
                                className="cwallet__depositBtn cwallet__depositBtn--secondary"
                                onClick={() => setStep(1)}
                                disabled={cardSubmitting}
                            >
                                Back
                            </button>
                            {/* Saved card: SavedCardPay portals its own Pay
                                button into this slot, so the row is [Back][Pay]
                                like every other step instead of a lone Back
                                with the Pay floating above (2026-08-14). */}
                            {paymentMethod === 'card' && savedCardActive ? (
                                <div className="cwallet__paySlot" ref={setPaySlot} />
                            ) : paymentMethod === 'card' && embeddedCardActive ? (
                                /* EMBEDDED rail: card entry gets its OWN step
                                   (2026-08-13 — no cramped scrolling). */
                                <button
                                    className="cwallet__depositBtn cwallet__depositBtn--primary"
                                    disabled={pendingCheckPending}
                                    onClick={() => setStep(6)}
                                >
                                    {savedCardActive ? 'Continue to payment' : 'Continue to card details'}
                                </button>
                            ) : paymentMethod === 'card' ? (
                                <button
                                    className="cwallet__depositBtn cwallet__depositBtn--primary"
                                    disabled={cardSubmitting || pendingCheckPending}
                                    onClick={handleCardCheckout}
                                >
                                    {/* Just the amount + method (owner 2026-07-19: the
                                        "Wallet top-up + invoice payment" suffix was "way
                                        too much text — we already explained it above"). */}
                                    {cardSubmitting
                                        ? 'Opening secure payment…'
                                        : pendingCheckPending
                                            ? 'Checking your account…'
                                            : `Pay ${money(includeInvoices ? totalTransfer : amount)} by Credit Card`}
                                </button>
                            ) : (
                                <button
                                    className="cwallet__depositBtn cwallet__depositBtn--primary"
                                    onClick={() => setStep(3)}
                                >
                                    Continue
                                </button>
                            )}
                        </div>

                    </>
                )}

                {/* Step 3: Bank Details + Upload */}
                {step === 3 && (
                    <>
                        <div className="cwallet__bankDetails">
                            {/* Split mode: the figure to transfer is X + P — the top-up plus the invoices riding on top. */}
                            <div className="cwallet__bankTitle">Transfer <strong>{money(totalTransfer)}</strong> to the following account:</div>
                            {splitActive && (
                                <div className="cwsplit__note">
                                    Includes your {money(splitP)} pending invoices on top of the {money(amount)} wallet top-up.
                                </div>
                            )}
                            <div className="cwallet__bankRow">
                                <span className="cwallet__bankLabel">Company</span>
                                <span className="cwallet__bankValue">Holiday Home Cleaning Services CO. L.L.C</span>
                            </div>
                            <div className="cwallet__bankRow">
                                <span className="cwallet__bankLabel">Bank</span>
                                <span className="cwallet__bankValue">Mashreq Bank PSC</span>
                            </div>
                            <div className="cwallet__bankRow">
                                <span className="cwallet__bankLabel">IBAN</span>
                                <span className="cwallet__bankValue">
                                    AE500330000019101412012
                                    <button className="cwallet__copyBtn" onClick={() => copyToClipboard('AE500330000019101412012')}>
                                        Copy
                                    </button>
                                </span>
                            </div>
                            <div className="cwallet__bankRow">
                                <span className="cwallet__bankLabel">Account No.</span>
                                <span className="cwallet__bankValue">
                                    019101412012
                                    <button className="cwallet__copyBtn" onClick={() => copyToClipboard('019101412012')}>
                                        Copy
                                    </button>
                                </span>
                            </div>
                            <div className="cwallet__bankRow">
                                <span className="cwallet__bankLabel">SWIFT Code</span>
                                <span className="cwallet__bankValue">BOMLAEAD</span>
                            </div>
                        </div>

                        <div className="cwallet__customInput">
                            <label className="cwallet__inputLabel">Bank Reference (optional)</label>
                            <input
                                type="text"
                                className="cwallet__input"
                                placeholder="Enter bank transfer reference"
                                value={bankReference}
                                onChange={(e) => setBankReference(e.target.value)}
                            />
                        </div>

                        <div className="cwallet__customInput" style={{ marginTop: 12 }}>
                            <label className="cwallet__inputLabel">Notes (optional)</label>
                            <textarea
                                className="cwallet__textarea"
                                placeholder="Any additional notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>

                        <div style={{ marginTop: 16 }}>
                            <label className="cwallet__inputLabel">Upload Proof of Payment *</label>
                            {!file ? (
                                <div {...getRootProps()} className={`cwallet__dropzone ${isDragActive ? 'is-active' : ''}`}>
                                    <input {...getInputProps()} />
                                    <div className="cwallet__dropzoneText">
                                        {isDragActive ? 'Drop the file here...' : 'Click or drag to upload proof of payment'}
                                    </div>
                                    <div className="cwallet__dropzoneHint">JPEG, PNG, WebP, or PDF (max 10MB)</div>
                                </div>
                            ) : (
                                <div className="cwallet__filePreview">
                                    <span className="cwallet__fileName">{file.name}</span>
                                    <button className="cwallet__fileRemove" onClick={() => setFile(null)}>Remove</button>
                                </div>
                            )}
                        </div>

                        <div className="cwallet__depositActions">
                            <button className="cwallet__depositBtn cwallet__depositBtn--secondary" onClick={() => setStep(2)}>
                                Back
                            </button>
                            <button
                                className="cwallet__depositBtn cwallet__depositBtn--primary"
                                disabled={!canProceedStep3}
                                onClick={() => setStep(4)}
                            >
                                Review
                            </button>
                        </div>
                    </>
                )}

                {/* Step 7: in-modal SUCCESS for card payments (2026-08-13) */}
                {step === 7 && cardPaid && (
                    <>
                        <div className="cwpaid">
                            <div className="cwpaid__badge" aria-hidden="true">✓</div>
                            <div className="cwpaid__title">Payment successful</div>
                            <div className="cwpaid__amount">{money(cardPaid.amount)}</div>
                            <div className="cwpaid__rows">
                                <div className="cwpaid__row">
                                    <span>Wallet top-up</span><strong>{money(cardPaid.topup)}</strong>
                                </div>
                                {cardPaid.bonus > 0 && (
                                    <div className="cwpaid__row cwpaid__row--bonus">
                                        <span>Bonus credit added</span><strong>+{money(cardPaid.bonus)}</strong>
                                    </div>
                                )}
                                {cardPaid.invoices > 0 && (
                                    <div className="cwpaid__row">
                                        <span>Pending invoices settled</span><strong>{money(cardPaid.invoices)}</strong>
                                    </div>
                                )}
                                {cardPaid.balance != null && (
                                    <div className="cwpaid__row cwpaid__row--total">
                                        <span>Available to spend now</span><strong>{money(cardPaid.balance)}</strong>
                                    </div>
                                )}
                            </div>
                            {/* One implementation of "name your new card",
                                shared with the invoice payment page — this
                                success step is where it was born. */}
                            {cardPaid.savedCard && (
                                <div className="cwpaid__namer">
                                    <SaveCardNamer card={cardPaid.savedCard} />
                                </div>
                            )}

                            <div className="cwpaid__note">
                                A receipt is on its way to you. You can use this balance for bookings right away.
                            </div>

                            {/* …and downloadable right here, rather than only in
                                the email (2026-08-14). Fetched as a blob by
                                ReceiptDownload, which says so when it cannot. */}
                            {/* The download button is HERE THE MOMENT THE
                                PAYMENT COMPLETES — no spinner gate, nothing
                                polled. It resolves the payment id when clicked
                                (2026-08-14). */}
                            {/* ONE PAYMENT, TWO RECEIPTS when invoices rode
                                along (2026-08-14): the capture creates two
                                Zoho payments — the top-up on the deposit, the
                                invoices on the settlement — so both are offered,
                                each labelled for what it covers. */}
                            {cardPaid.invoices > 0.009 ? (
                                <>
                                    <div className="cwpaid__namer">
                                        <ReceiptDownload
                                            href={receiptHref(cardPaid.deposit_id, cardPaid.order_ref, 'deposit')}
                                            label={`Open top-up receipt (${money(cardPaid.topup)})`}
                                        />
                                    </div>
                                    <div className="cwpaid__namer">
                                        <ReceiptDownload
                                            href={receiptHref(cardPaid.deposit_id, cardPaid.order_ref, 'settlement')}
                                            label={`Open invoice payment receipt (${money(cardPaid.invoices)})`}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="cwpaid__namer">
                                    <ReceiptDownload
                                        href={receiptHref(cardPaid.deposit_id, cardPaid.order_ref)}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="cwallet__depositActions">
                            <button
                                className="cwallet__depositBtn cwallet__depositBtn--primary"
                                onClick={() => {
                                    if (typeof onSuccess === 'function') {
                                        onSuccess(cardPaid.deposit_id ? { id: cardPaid.deposit_id } : cardPaid);
                                    }
                                    onClose();
                                }}
                            >
                                Done
                            </button>
                        </div>
                    </>
                )}

                {/* Step 6: EMBEDDED card entry — a full step of its own (the owner
                    2026-08-13): the totals up top, the card fields with room
                    to breathe, save-card consent, and Pay. Mounting on step
                    entry also gives the SDK a fresh auth window every time. */}
                {step === 6 && (
                    <>
                        <div className="cwallet__inputLabel">Card Payment</div>
                        {/* The figure being charged, stated on the step that
                            charges it (2026-08-14) — the same block the
                            invoice payment page carries above its card fields.
                            The Pay button repeats it, but a client should not
                            have to read a button to know the amount. */}
                        <div className="cwallet__cardAmount">
                            <span>To pay by card</span>
                            <strong>{money(splitActive ? totalTransfer : amount)}</strong>
                        </div>
                        {/* No totals recap here (the owner: waste of space) — the Pay
                            button itself carries the amount; the split breakdown
                            already lives on step 2. */}
                        {splitActive && (
                            <div className="cwsplit__note">
                                Includes your {money(splitP)} pending invoices on top of the {money(amount)} top-up.
                            </div>
                        )}

                        {(
                        <HostedCardPanel
                            payLabel={`Pay ${money(splitActive ? totalTransfer : amount)}`}
                            amountAed={splitActive ? totalTransfer : amount}
                            disabled={pendingCheckPending || (!purpose && amount < MIN_TOPUP)}
                            actionSlot={cardPaySlot}
                            startPayment={startHostedPayment}
                            onUnavailable={() => { setHostedRail('off'); setStep(2); }}
                            onPaid={async (result) => {
                                // Read the wallet back so the figure shown is the
                                // SERVER's, never a local guess at the arithmetic.
                                let fresh = null;
                                try {
                                    const w = await axios.get('/api/client-requests/wallet', walletQuery);
                                    fresh = w.data || null;
                                    setWallet(fresh);
                                } catch { /* balance line simply omitted */ }
                                // If they asked us to save the card, find it so
                                // they can name it right here (2026-08-13) —
                                // naming is far more likely at the moment of
                                // saving than on a later trip to Account.
                                let savedCard = null;
                                if (saveNewCard) {
                                    try {
                                        const c = await axios.get('/api/cards');
                                        const list = Array.isArray(c.data?.cards) ? c.data.cards : [];
                                        savedCard = list[0] || null; // newest first
                                    } catch { /* naming simply isn't offered */ }
                                }
                                setCardPaid({
                                    amount: splitActive ? totalTransfer : amount,
                                    topup: amount,
                                    invoices: splitActive ? splitP : 0,
                                    bonus: bonus.bonus_amount || 0,
                                    balance: fresh ? availableBalance(fresh) : null,
                                    deposit_id: result?.deposit_id || null,
                                    order_ref: result?.order_ref || null,
                                    savedCard,
                                });
                                setStep(7);
                            }}
                            onFailed={() => { /* panel shows the message inline; client can retry */ }}
                        >
                        <label className="hcard-save">
                            <input
                                type="checkbox"
                                checked={saveNewCard}
                                onChange={(e) => setSaveNewCard(e.target.checked)}
                            />
                            <span>Save this card for future payments</span>
                        </label>
                        {saveNewCard && (
                            <div className="hcard-consent hcard-consent--readonly">
                                <span className="hcard-consent-text">
                                    <span className="hcard-consent-mandate">{CARD_CONSENT_TEXT}</span>
                                </span>
                            </div>
                        )}
                        </HostedCardPanel>
                        )}

                        {/* [Back][Pay] — the embedded panel portals its Pay
                            button into the slot, so this step ends in the same
                            row shape as every other one. */}
                        <div className="cwallet__depositActions">
                            <button
                                className="cwallet__depositBtn cwallet__depositBtn--secondary"
                                onClick={() => setStep(2)}
                                disabled={cardSubmitting}
                            >
                                Back
                            </button>
                            <div className="cwallet__paySlot" ref={setCardPaySlot} />
                        </div>
                    </>
                )}

                {/* Step 4: Confirm */}
                {step === 4 && (
                    <>
                        <div className="cwallet__inputLabel">Review Your Deposit</div>
                        <div className="cwallet__summary">
                            {splitActive ? (
                                <>
                                    <div className="cwallet__summaryRow">
                                        <span>Wallet Top-up</span>
                                        <span>{money(amount)}</span>
                                    </div>
                                    <div className="cwallet__summaryRow">
                                        <span>Pending Invoice Payment (settled on top)</span>
                                        <span>{money(splitP)}</span>
                                    </div>
                                    <div className="cwallet__summaryRow cwallet__summaryRow--transferTotal">
                                        <span>Total to Transfer</span>
                                        <span>{money(totalTransfer)}</span>
                                    </div>
                                </>
                            ) : (
                                <div className="cwallet__summaryRow">
                                    <span>Deposit Amount</span>
                                    <span>{money(amount)}</span>
                                </div>
                            )}
                            {bonus.bonus_percentage > 0 && (
                                <div className="cwallet__summaryRow">
                                    <span>Bonus ({bonus.bonus_percentage}%{splitActive ? ' of the top-up' : ''})</span>
                                    <span>+ {money(bonus.bonus_amount)}</span>
                                </div>
                            )}
                            <div className="cwallet__summaryRow">
                                <span>Payment Method</span>
                                <span>Bank Transfer</span>
                            </div>
                            {bankReference && (
                                <div className="cwallet__summaryRow">
                                    <span>Bank Reference</span>
                                    <span>{bankReference}</span>
                                </div>
                            )}
                            <div className="cwallet__summaryRow">
                                <span>Proof of Payment</span>
                                <span>{file?.name}</span>
                            </div>
                            <div className="cwallet__summaryRow">
                                <span>{splitActive ? 'Wallet Credit (top-up + bonus)' : 'Total Credit'}</span>
                                <span>{money(bonus.total_credit)}</span>
                            </div>
                        </div>

                        <div className="cwallet__depositActions">
                            <button className="cwallet__depositBtn cwallet__depositBtn--secondary" onClick={() => setStep(3)}>
                                Back
                            </button>
                            <button
                                className="cwallet__depositBtn cwallet__depositBtn--primary"
                                disabled={submitting}
                                onClick={handleSubmit}
                            >
                                {submitting ? 'Submitting...' : 'Submit Deposit Request'}
                            </button>
                        </div>
                    </>
                )}

                {/* Step 5: Submitted — PERSISTENT confirmation (standalone
                    Add-Funds flow only; stays until Done is clicked). */}
                {step === 5 && (
                    <div className="cwallet__submitted">
                        <div className="cwallet__submittedIcon" aria-hidden="true">✓</div>
                        <div className="cwallet__submittedTitle">Deposit request submitted</div>
                        <p className="cwallet__submittedText">
                            {splitActive
                                ? `${money(splitP)} settles your pending invoices and ${money(amount)} tops up your wallet once our team confirms the transfer.`
                                : 'Your transfer details are with our team — the amount is credited to your wallet as soon as the transfer is confirmed.'}
                        </p>
                        <div className="cwallet__depositActions cwallet__depositActions--center">
                            <button className="cwallet__depositBtn cwallet__depositBtn--primary" onClick={closeOrFinish}>
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
